import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { ProblemStatementAssistant } from '../components/ProblemStatementAssistant';

function CreateProblem() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPSA, setShowPSA] = useState(false);

  const handlePSAComplete = async (psaTitle: string, psaDescription: string) => {
    setTitle(psaTitle);
    setDescription(psaDescription);
    setShowPSA(false);

    // Automatically create the problem
    try {
      setLoading(true);
      setError(null);
      const { problem } = await api.createProblem({
        title: psaTitle,
        description: psaDescription
      });
      navigate(`/problems/${problem.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create problem');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { problem } = await api.createProblem({ title, description });
      navigate(`/problems/${problem.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create problem');
    } finally {
      setLoading(false);
    }
  };

  // If PSA is active, show it full-screen
  if (showPSA) {
    return (
      <ProblemStatementAssistant
        onComplete={handlePSAComplete}
        onCancel={() => setShowPSA(false)}
      />
    );
  }

  return (
    <div>
      <header className="header">
        <div className="header-content">
          <Link to="/" className="logo">Attack100</Link>
          <Link to="/" className="btn btn-secondary">
            Cancel
          </Link>
        </div>
      </header>

      <div className="container" style={{ maxWidth: '600px' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem' }}>
          Create New Problem
        </h1>

        {/* PSA Option */}
        <div className="card" style={{ marginBottom: '2rem', border: '2px solid var(--primary)', background: 'var(--gray-50)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem' }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--gray-900)' }}>
                ✨ Problem Statement Assistant
              </h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0', lineHeight: '1.5' }}>
                Get AI help to craft an exquisite problem statement through conversation. Better problem = better angles.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowPSA(true)}
              className="btn btn-primary"
              style={{ whiteSpace: 'nowrap' }}
            >
              Launch Assistant
            </button>
          </div>
        </div>

        <div style={{ textAlign: 'center', margin: '1.5rem 0', color: 'var(--gray-400)', fontSize: '0.875rem', fontWeight: '500' }}>
          OR
        </div>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="card">
            <div className="input-group">
              <label className="label" htmlFor="title">
                Problem Title
              </label>
              <input
                id="title"
                type="text"
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Build a SaaS Product"
                required
              />
            </div>

            <div className="input-group">
              <label className="label" htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                className="textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the problem you want to solve..."
                required
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Link to="/" className="btn btn-secondary">
                Cancel
              </Link>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Creating...' : 'Create Problem'}
              </button>
            </div>
          </div>
        </form>

      </div>
    </div>
  );
}

export default CreateProblem;
